import { useDebounce } from '@/hooks/use-debounce';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  Skeleton,
} from '@opaz/ui-components';
import { useSearch } from '@/hooks/useQueries';
import { Search } from '@/assets/icons';
import { useAnalytics } from '@/hooks/useAnalytics';

export function ServiceAutocomplete() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const analytics = useAnalytics();

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const lastTrackedSearchUsedRef = useRef<string>('');
  const lastTrackedSearchRef = useRef<string>('');

  const debouncedSearch = useDebounce(searchValue, 500);

  useEffect(() => {
    lastTrackedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const { data, isLoading } = useSearch(debouncedSearch);

  const items = useMemo(() => {
    return (
      data?.data?.map((item) => ({
        value: item.documentId,
        label: item.title,
      })) ?? []
    );
  }, [data]);

  const labels = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc[item.value] = item.label;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [items]);

  const reset = () => {
    setSelectedValue('');
    setSearchValue('');
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.relatedTarget?.hasAttribute('cmdk-list') && labels[selectedValue] !== searchValue) {
      reset();
    }
  };

  if (debouncedSearch !== lastTrackedSearchRef.current) {
    analytics.trackSiteSearch(debouncedSearch);
  }

  // Track search_used when search results are available
  useEffect(() => {
    if (
      debouncedSearch &&
      debouncedSearch.length > 2 &&
      !isLoading &&
      data !== undefined &&
      debouncedSearch !== lastTrackedSearchUsedRef.current &&
      debouncedSearch !== labels[selectedValue]
    ) {
      const resultsCount = data?.data?.length ?? 0;
      analytics.trackSearchUsed(debouncedSearch, resultsCount);
      lastTrackedSearchUsedRef.current = debouncedSearch;
    }
  }, [analytics, debouncedSearch, isLoading, data, labels, selectedValue]);

  const handleSelect = (inputValue: string) => {
    if (inputValue === selectedValue) {
      reset();
    } else {
      setSelectedValue(inputValue);
      setSearchValue(labels[inputValue] ?? '');
      navigate({ to: `/services/services-catalogue/${inputValue}` });
    }
    setOpen(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setOpen(e.key !== 'Escape');
  };

  const handleInputMouseDown = () => {
    setOpen((open) => !!searchValue || !open);
  };

  const handleInputFocus = () => {
    setOpen(true);
  };

  return (
    <div className="flex items-center w-full md:w-8/12">
      <Popover open={open} onOpenChange={setOpen}>
        <Command
          className="rounded-full border border-base-border focus-within:ring-2 focus-within:ring-purple-500 focus-within:p-1 transition-all"
          shouldFilter={false}
        >
          <PopoverTrigger asChild>
            <div className="relative w-full">
              <SearchInput
                ref={inputRef}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onMouseDown={handleInputMouseDown}
                onFocus={handleInputFocus}
                onBlur={handleBlur}
                className="h-11"
                aria-label={t('services.searchAriaLabel')}
                placeholder={t('services.searchPlaceholder')}
              />

              {/* 🔍 Floating Search Button */}
              <Button
                type="button"
                aria-hidden="true"
                className="absolute top-1/2 -translate-y-1/2 bg-brand-medium text-white bg-[#005287] rounded-full min-w-10 w-10 h-10 mx-1 end-0"
                onClick={() => setOpen(true)}
              >
                <Search className="w-5 h-5" />
              </Button>
            </div>
          </PopoverTrigger>

          {!open && <CommandList aria-hidden="true" className="hidden" />}

          <PopoverContent
            role="listbox"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (e.target instanceof Element && e.target.closest('[role="searchbox"]')) {
                e.preventDefault();
              }
            }}
            className="w-[--radix-popover-trigger-width] p-0"
          >
            <CommandList>
              {isLoading && (
                <div className="p-1">
                  <Skeleton className="h-6 w-full" />
                </div>
              )}
              {!isLoading && items.length > 0 && (
                <CommandGroup>
                  {items.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value.toString()}
                      onMouseDown={(e) => e.preventDefault()}
                      onSelect={handleSelect}
                    >
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {!isLoading && items.length === 0 && <CommandEmpty>{t('search.noResults')}</CommandEmpty>}
            </CommandList>
          </PopoverContent>
        </Command>
      </Popover>
    </div>
  );
}
